import { useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import { ButtonGroup, Dropdown } from "react-bootstrap";
import { FiCheck, FiMoreVertical, FiSearch, FiX } from "react-icons/fi";

import { TableComponent } from "../../../shared/components/table";
import { AlertMsg } from "../../../shared/components/alert-msg";
import AddIco from "../../../assets/icons/add.png";
import ShopIco from "../../../assets/shop_1.jpeg";
import { appActions } from "../../../redux/actions/in-app";
import { LoadingItem } from "../../../shared/components/loading";
import { appConstants } from "../../../redux/action-constants/inapp.constants";
import { getDateFromISOOne } from "../../../shared/utils";
import { useWindowSize } from "../../../hooks/useWindowSize";
import CustomPagination from "../../../shared/components/pagination/CustomPagination";
import styles from "./product-listing.module.scss";
import FilterModal from "./FilterModal";

import "../index.scss";
import "../../../scss/product-listing.scss";

const inventoryTabs = ["All", "Shown", "Hidden", "Trash"];
const inventoryStatusParams: any = {
  Shown: "Active",
  Hidden: "InActive",
  Trash: "IsDeleted",
};

const defaultProductFilterValues = {
  productNames: "",
  category: [],
  status: "All",
  minPrice: "0",
  maxPrice: "1000",
  lowStockOnly: false,
};

const getProductsListAction = async ({ pageProps, payload }: any) => {
  await pageProps.getProductsListAction(payload);
};

const deleteProductAction = async ({ pageProps, payload }: any) => {
  await pageProps.updateProductAction(payload, "delete");
};

const formatPrice = (product: any) => {
  const amount = product?.price?.amount ?? product?.newPrice ?? 0;
  const numberAmount = Number(amount);

  if (Number.isNaN(numberAmount)) {
    return "$0.00";
  }

  return `$${numberAmount.toFixed(2)}`;
};

const getCategoryOptions = (productsList: any) => {
  const aggregateResults =
    productsList?.aggregateResults || productsList?.aggregates || [];
  const categoriesData = Array.isArray(aggregateResults)
    ? aggregateResults.find((item: any) => item?.name === "Categories")
    : null;

  if (Array.isArray(categoriesData?.buckets)) {
    return categoriesData.buckets
      .map((bucket: any) => bucket?.key || bucket?.value)
      .filter(Boolean)
      .map((category: string) => ({
        label: category,
        value: category,
      }));
  }

  const categories = new Set<string>();

  if (Array.isArray(productsList?.results)) {
    productsList.results.forEach((product: any) => {
      if (Array.isArray(product?.categories)) {
        product.categories.forEach((category: string) => {
          if (category) {
            categories.add(category);
          }
        });
      }
    });
  }

  return Array.from(categories).map((category) => ({
    label: category,
    value: category,
  }));
};

const getProductStatus = (
  product: any,
  localProductStatuses: any,
  productKey = product?.id,
) => {
  if (productKey && typeof localProductStatuses[productKey] === "boolean") {
    return localProductStatuses[productKey];
  }

  return typeof product?.isActive === "boolean" ? product.isActive : false;
};

const SuspendStoreModal = (props: any) => {
  let {
    showSuspendModal,
    setShowSuspendModal,
    pageProps,
    productStoreId,
    productId,
    productName,
  } = props;

  const deleteProcess: any = (payload: any) => {
    deleteProductAction({
      pageProps,
      payload,
    }).then(() => {});
  };

  return (
    <Modal
      className="suspend_wrap"
      show={showSuspendModal}
      onHide={() => {
        if (!pageProps?.updateStoreProductRequest?.is_request_processing) {
          setShowSuspendModal(false);
        }
      }}
    >
      <Modal.Header closeButton>Delete {productName}?</Modal.Header>
      <Modal.Body>
        {pageProps?.updateStoreProductRequest?.request_status !==
          appConstants.UPDATE_PRODUCT_SUCCESS && (
          <>
            <div className="head_message">
              Please be advised that deleting this product will means that{" "}
              <span> customers will be unable to purchase this product</span>.
              <br /> Are you sure you want to proceed with this action?
            </div>
            {pageProps?.updateStoreProductRequest?.request_status &&
              pageProps.updateStoreProductRequest.request_status ===
                appConstants.UPDATE_PRODUCT_FAILURE && (
                <AlertMsg
                  type="error"
                  message={
                    pageProps.updateStoreProductRequest?.request_data.error
                  }
                />
              )}
            <div className="suspend_ctas">
              <button
                className="btn cancel"
                disabled={
                  pageProps.updateStoreProductRequest?.is_request_processing
                }
                onClick={() => setShowSuspendModal(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn confirm"
                onClick={() => {
                  let payload = {
                    storeId: productStoreId,
                    productId: productId,
                  };
                  deleteProcess(payload);
                }}
                disabled={
                  pageProps.updateStoreProductRequest?.is_request_processing
                }
              >
                {pageProps.updateStoreProductRequest.is_request_processing
                  ? "Please wait..."
                  : "Confirm"}
              </button>
            </div>
          </>
        )}
        {pageProps?.updateStoreProductRequest?.request_status ===
          appConstants.UPDATE_PRODUCT_SUCCESS && (
          <AlertMsg type="success" message="Product was successfully deleted" />
        )}
      </Modal.Body>
    </Modal>
  );
};

const InAppContent = ({ pageProps }: any) => {
  const screenResolution: any = useWindowSize();
  const { storeId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const initialPage = parseInt(queryParams.get("page") as string) || 1;

  const [pageNumber, setPageNumber] = useState(initialPage);
  const [pageSize] = useState(10);
  const [showSuspendModal, setShowSuspendModal] = useState<boolean>(false);
  const [productId, setProductId] = useState();
  const [productStoreId, setProductStoreId] = useState();
  const [productName, setProductName] = useState();
  const [searchText, setSearchText] = useState<string>("");
  const [searchValue, setSearchValue] = useState<string>("");
  const [activeTab, setActiveTab] = useState("All");
  const [sortConfig, setSortConfig] = useState<any>({
    key: "",
    direction: "asc",
  });
  const [localProductStatuses, setLocalProductStatuses] = useState<any>({});
  const [selectedProductKeys, setSelectedProductKeys] = useState<any>({});
  const [toastInfo, setToastInfo] = useState<any>(null);
  const [displayedProductsList, setDisplayedProductsList] = useState<any>(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [categoryList, setCategoryList] = useState<
    { label: string; value: string }[]
  >([]);
  const [activeFilters, setActiveFilters] = useState<any>(null);
  const [productFilterValues, setProductFilterValues] = useState<{
    productNames: string;
    category: string[];
    status: string;
    minPrice: string;
    maxPrice: string;
    lowStockOnly: boolean;
  }>(defaultProductFilterValues);

  const productsList =
    pageProps.getProductsListRequest?.request_data?.response?.data;
  const requestStatus = pageProps.getProductsListRequest?.request_status;
  const isProductsLoading = requestStatus === appConstants.GET_PRODUCTS_LIST_PENDING;
  const isInitialProductsLoading =
    !displayedProductsList &&
    requestStatus !== appConstants.GET_PRODUCTS_LIST_FAILURE;
  const allProducts = displayedProductsList?.results || [];

  useEffect(() => {
    if (productsList) {
      setDisplayedProductsList(productsList);

      const nextCategoryOptions = getCategoryOptions(productsList);
      if (
        nextCategoryOptions.length > 0 &&
        !activeFilters &&
        !searchText
      ) {
        setCategoryList(nextCategoryOptions);
      }
    }
  }, [productsList, activeFilters, searchText]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    params.set("page", pageNumber.toString());
    navigate(`?${params.toString()}`, { replace: true });
  }, [pageNumber, location.search, navigate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPageNumber(1);
      setSearchText(searchValue.trim());
    }, 300);

    return () => clearTimeout(timer);
  }, [searchValue]);

  useEffect(() => {
    let filterInfo = `?${storeId ? `StoreId=${storeId}&` : ""}Page=${pageNumber}&Size=${pageSize}`;

    if (activeFilters) {
      if (activeFilters.productNames?.trim()) {
        filterInfo += `&SearchText=${encodeURIComponent(
          activeFilters.productNames.trim(),
        )}`;
      }

      if (activeFilters.minPrice != null) {
        const hasPriceFilter =
          activeFilters.minPrice !== Number(defaultProductFilterValues.minPrice) ||
          activeFilters.maxPrice !== Number(defaultProductFilterValues.maxPrice);

        if (hasPriceFilter) {
          filterInfo += `&PriceFrom=${activeFilters.minPrice}`;
        }
      }

      if (activeFilters.maxPrice != null) {
        const hasPriceFilter =
          activeFilters.minPrice !== Number(defaultProductFilterValues.minPrice) ||
          activeFilters.maxPrice !== Number(defaultProductFilterValues.maxPrice);

        if (hasPriceFilter) {
          filterInfo += `&PriceTo=${activeFilters.maxPrice}`;
        }
      }

      if (activeFilters.status === "Active") {
        filterInfo += "&Status=Active";
      } else if (activeFilters.status === "Inactive") {
        filterInfo += "&Status=InActive";
      }

      if (activeFilters.lowStockOnly === true) {
        filterInfo += "&LowStockOnly=true";
      } else if (activeFilters.lowStockOnly === false) {
        filterInfo += "&LowStockOnly=false";
      }

      if (
        Array.isArray(activeFilters.category) &&
        activeFilters.category.length > 0 &&
        activeFilters.category[0] !== "All"
      ) {
        activeFilters.category.forEach((category: string) => {
          filterInfo += `&Categories=${encodeURIComponent(category)}`;
        });
      }
    } else {
      const statusParam = inventoryStatusParams[activeTab];

      if (statusParam) {
        filterInfo += `&Status=${statusParam}`;
      }

      if (searchText) {
        filterInfo += `&SearchText=${encodeURIComponent(searchText)}`;
      }
    }

    getProductsListAction({
      pageProps,
      payload: filterInfo,
    }).then(() => {});
  }, [pageNumber, pageSize, searchText, activeTab, storeId, activeFilters]);

  useEffect(() => {
    if (toastInfo) {
      const timer = setTimeout(() => setToastInfo(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toastInfo]);

  const sortedProducts = useMemo(() => {
    if (!sortConfig.key) {
      return allProducts;
    }

    return [...allProducts].sort((firstProduct: any, secondProduct: any) => {
      let firstValue = firstProduct?.[sortConfig.key];
      let secondValue = secondProduct?.[sortConfig.key];

      if (sortConfig.key === "price") {
        firstValue = firstProduct?.price?.amount ?? firstProduct?.newPrice ?? 0;
        secondValue =
          secondProduct?.price?.amount ?? secondProduct?.newPrice ?? 0;
      }

      if (sortConfig.key === "availableQuantity") {
        firstValue = Number(firstValue || 0);
        secondValue = Number(secondValue || 0);
      }

      if (typeof firstValue === "string" && typeof secondValue === "string") {
        return sortConfig.direction === "asc"
          ? firstValue.localeCompare(secondValue)
          : secondValue.localeCompare(firstValue);
      }

      return sortConfig.direction === "asc"
        ? Number(firstValue || 0) - Number(secondValue || 0)
        : Number(secondValue || 0) - Number(firstValue || 0);
    });
  }, [allProducts, sortConfig]);

  const handlePageClick = (event: any) => {
    setPageNumber(event?.selected + 1);
  };

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
    setActiveFilters(null);
    setPageNumber(1);
  };

  const handleOpenFilterModal = () => {
    const nextCategoryOptions = getCategoryOptions(displayedProductsList);
    if (categoryList.length === 0 && nextCategoryOptions.length > 0) {
      setCategoryList(nextCategoryOptions);
    }
    setShowFilterModal(true);
  };

  const handleSort = (key: string) => {
    setSortConfig((currentSort: any) => ({
      key,
      direction:
        currentSort.key === key && currentSort.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const getProductKey = (product: any, index: any) =>
    product?.id || `${product?.storeId}-${index}`;

  const selectedVisibleCount = sortedProducts.filter((product: any, index: any) =>
    selectedProductKeys[getProductKey(product, index)],
  ).length;
  const allVisibleProductsSelected =
    sortedProducts.length >= 1 && selectedVisibleCount === sortedProducts.length;
  const someVisibleProductsSelected =
    selectedVisibleCount > 0 && !allVisibleProductsSelected;

  const handleSelectAllProducts = () => {
    setSelectedProductKeys((currentSelectedProducts: any) => {
      const nextSelectedProducts = { ...currentSelectedProducts };

      sortedProducts.forEach((product: any, index: any) => {
        const productKey = getProductKey(product, index);

        if (allVisibleProductsSelected) {
          delete nextSelectedProducts[productKey];
        } else {
          nextSelectedProducts[productKey] = true;
        }
      });

      return nextSelectedProducts;
    });
  };

  const handleSelectProduct = (productKey: any) => {
    setSelectedProductKeys((currentSelectedProducts: any) => ({
      ...currentSelectedProducts,
      [productKey]: !currentSelectedProducts[productKey],
    }));
  };

  const getSortAria = (key: string) => {
    if (sortConfig.key !== key) {
      return "none";
    }

    return sortConfig.direction === "asc" ? "ascending" : "descending";
  };

  const toggleProductStatus = (product: any, productKey: any) => {
    const currentStatus = getProductStatus(
      product,
      localProductStatuses,
      productKey,
    );
    const nextStatus = !currentStatus;
    const payload = {
      ...product,
      id: product?.id,
      storeId: product?.storeId,
      isActive: nextStatus,
    };

    setLocalProductStatuses((currentStatuses: any) => ({
      ...currentStatuses,
      [productKey]: nextStatus,
    }));

    pageProps
      .updateProductAction(payload, "toggle-status")
      .then(() => {
        setToastInfo({
          type: "success",
          message: `${product?.name || "Product"} is ${
            nextStatus ? "active" : "inactive"
          }`,
        });
      })
      .catch((error: any) => {
        setLocalProductStatuses((currentStatuses: any) => ({
          ...currentStatuses,
          [productKey]: currentStatus,
        }));
        setToastInfo({
          type: "error",
          message:
            error?.error ||
            error?.message ||
            `Could not update ${product?.name || "product"} status`,
        });
      });
  };

  const MobileDataGrid = () => {
    return (
      <div className="mobile-data-grid">
        {allProducts.length >= 1 && (
          <>
            {allProducts.map((eachItem: any, index: any) => (
              <div className="each_data_item" key={index}>
                <div className="main-txt-head">
                  <div className="prod_def">
                    <div className="prod_img">
                      <img src={eachItem?.urls?.[0] || ShopIco} alt="" />
                    </div>
                    <div className="prod_name_cat">
                      <div className="name_only">{eachItem?.name}</div>
                      <div className="all_cat_list">
                        {eachItem?.categories?.map((eachCat: any, idx: any) => (
                          <span className="cate" key={idx}>
                            {eachCat}
                            {idx + 1 < eachItem.categories.length ? "," : ""}
                          </span>
                        ))}
                        {!eachItem?.categories ||
                        eachItem.categories.length === 0
                          ? "N/A"
                          : ""}
                      </div>
                    </div>
                  </div>
                  <div className="other_action">
                    <div className="product-actions">
                      <Dropdown className="report_dropdown" as={ButtonGroup}>
                        <Dropdown.Toggle
                          id={`mobile-product-actions-${eachItem?.id || index}`}
                          className="action-cta"
                          aria-label={`Open actions for ${eachItem?.name}`}
                        >
                          <FiMoreVertical aria-hidden="true" />
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            href={`/app/stores/${eachItem?.storeId}/products/${eachItem?.id}/edit`}
                          >
                            Edit
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  </div>
                </div>
                <div className="each_mobile_prod_info">
                  <div className="title-txt">Store</div>
                  <div className="availables">{eachItem.storeName}</div>
                </div>
                <div className="each_mobile_prod_info">
                  <div className="title-txt">Price</div>
                  <div className="availables price_txt">
                    {formatPrice(eachItem)}
                  </div>
                </div>
                <div className="each_mobile_prod_info">
                  <div className="title-txt">Created on</div>
                  <div className="availables">
                    <div className="date-item">
                      {getDateFromISOOne({
                        date: eachItem.createdDate,
                        returnTime: false,
                        hasDivider: true,
                      }) || "N/A"}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    );
  };

  const ReportTable = () => {
    return (
      <>
        <thead>
          <tr>
            <th className={styles.checkboxHeader}>
              <input
                type="checkbox"
                aria-label="Select all products"
                checked={allVisibleProductsSelected}
                ref={(inputElement) => {
                  if (inputElement) {
                    inputElement.indeterminate = someVisibleProductsSelected;
                  }
                }}
                onChange={handleSelectAllProducts}
              />
            </th>
            <th aria-sort={getSortAria("name") as any}>
              <button
                type="button"
                className={styles.sortButton}
                onClick={() => handleSort("name")}
                aria-label="Sort products by product name"
              >
                Product <span aria-hidden="true">↕</span>
              </button>
            </th>
            <th aria-sort={getSortAria("availableQuantity") as any}>
              <button
                type="button"
                className={styles.sortButton}
                onClick={() => handleSort("availableQuantity")}
                aria-label="Sort products by quantity"
              >
                Quantity <span aria-hidden="true">↕</span>
              </button>
            </th>
            <th aria-sort={getSortAria("price") as any}>
              <button
                type="button"
                className={styles.sortButton}
                onClick={() => handleSort("price")}
                aria-label="Sort products by price"
              >
                Price <span aria-hidden="true">↕</span>
              </button>
            </th>
            <th>Category</th>
            <th>Store Name</th>
            <th>Show/Hide</th>
            <th className={styles.actionsHeader}>
              <FiMoreVertical aria-hidden="true" />
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedProducts.map((eachItem: any, index: any) => {
            const productKey = getProductKey(eachItem, index);
            const isActive = getProductStatus(
              eachItem,
              localProductStatuses,
              productKey,
            );

            return (
              <tr key={productKey}>
                <td className={styles.checkboxCell}>
                  <input
                    type="checkbox"
                    aria-label={`Select ${eachItem?.name || "product"}`}
                    checked={!!selectedProductKeys[productKey]}
                    onChange={() => handleSelectProduct(productKey)}
                  />
                </td>
                <td>
                  <div className={styles.productCell}>
                    <img
                      src={
                        eachItem?.urls && eachItem?.urls.length >= 1
                          ? eachItem?.urls[0]
                          : ShopIco
                      }
                      alt=""
                    />
                    <div className={styles.productName}>{eachItem?.name}</div>
                  </div>
                </td>
                <td className={styles.quantityCell}>
                  {eachItem?.availableQuantity ?? 0}
                </td>
                <td className={styles.priceCell}>{formatPrice(eachItem)}</td>
                <td>
                  <div className={styles.categoryList}>
                    {eachItem?.categories && eachItem?.categories.length >= 1 ? (
                      eachItem.categories.map((eachCat: any, idx: any) => (
                        <span className={styles.categoryBadge} key={idx}>
                          {eachCat}
                        </span>
                      ))
                    ) : (
                      <span className={styles.categoryBadge}>N/A</span>
                    )}
                  </div>
                </td>
                <td className={styles.storeCell}>{eachItem?.storeName}</td>
                <td>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isActive}
                    aria-label={`${isActive ? "Hide" : "Show"} ${
                      eachItem?.name || "product"
                    }`}
                    className={`${styles.toggleSwitch} ${
                      isActive ? styles.toggleSwitchActive : ""
                    }`}
                    onClick={() => toggleProductStatus(eachItem, productKey)}
                  >
                    <span className={styles.toggleThumb}>
                      {isActive ? (
                        <FiCheck aria-hidden="true" />
                      ) : (
                        <FiX aria-hidden="true" />
                      )}
                    </span>
                  </button>
                </td>
                <td className={styles.actionCell}>
                  <Dropdown className={styles.rowActions} as={ButtonGroup}>
                    <Dropdown.Toggle
                      id={`product-actions-${productKey}`}
                      className={styles.actionButton}
                      aria-label={`Open actions for ${eachItem?.name}`}
                    >
                      <FiMoreVertical aria-hidden="true" />
                    </Dropdown.Toggle>
                    <Dropdown.Menu align="end">
                      <Dropdown.Item
                        as={Link}
                        to={`/app/stores/${eachItem?.storeId}/products/${eachItem?.id}/edit`}
                      >
                        Edit
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() =>
                          toggleProductStatus(eachItem, productKey)
                        }
                      >
                        {isActive ? "Hide" : "Show"}
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() => {
                          deleteProductAction({
                            pageProps,
                            payload: "CLEAR",
                          });
                          setProductId(eachItem?.id);
                          setProductName(eachItem?.name);
                          setProductStoreId(eachItem?.storeId);
                          setShowSuspendModal(true);
                        }}
                      >
                        Delete
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </td>
              </tr>
            );
          })}
        </tbody>
      </>
    );
  };

  const ReportsDisplay = () => {
    return (
      <div>
        {toastInfo && (
          <AlertMsg
            type={toastInfo.type}
            message={toastInfo.message}
            toast={true}
          />
        )}
        {showSuspendModal && productStoreId && productId && productName && (
          <SuspendStoreModal
            productName={productName}
            productId={productId}
            pageProps={pageProps}
            productStoreId={productStoreId}
            showSuspendModal={showSuspendModal}
            setShowSuspendModal={setShowSuspendModal}
          />
        )}
        <div className="report_head_wrap">
          <div className="report_head_left">
            <div className="report_heading">
              {storeId ? pageProps?.storeData?.name : "Products"}{" "}
              <span>
                {displayedProductsList?.totalNumberOfRecords?.toLocaleString()}
              </span>
            </div>
            <div className="report_desc">
              Easily add, edit and delete products from vendors&apos; stores
            </div>
          </div>
          {storeId && (
            <div className="report_head_right">
              <div className="report_head_cta">
                <Link
                  state={{
                    storeData: { storeName: pageProps?.storeData?.name },
                  }}
                  className="head_cta_link"
                  to={`/app/stores/${storeId}/products/create-new`}
                >
                  <button>
                    <img src={AddIco} alt="" />
                    <span>New Product</span>
                  </button>
                </Link>
              </div>
            </div>
          )}
        </div>
        <div className={`${styles.inventoryTableWrap} reports_table`}>
          <div className={styles.inventoryHeader}>
            <div className={styles.inventoryTabs} role="tablist">
              {inventoryTabs.map((tab) => (
                <button
                  type="button"
                  key={tab}
                  role="tab"
                  aria-selected={activeTab === tab}
                  className={activeTab === tab ? styles.activeTab : ""}
                  onClick={() => handleTabClick(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>
            <div className={styles.inventoryToolbar}>
              <div className={styles.searchWrap}>
                <FiSearch aria-hidden="true" />
                <input
                  type="text"
                  value={searchValue}
                  className={styles.searchInput}
                  placeholder="Search inventory"
                  aria-label="Search inventory"
                  onChange={(e: any) => {
                    setSearchValue(e.target.value);
                    setActiveFilters(null);
                  }}
                />
              </div>
              <button
                className={styles.filterButton}
                type="button"
                onClick={handleOpenFilterModal}
              >
                <span>Filter by</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="black"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="4" y1="6" x2="20" y2="6" />
                  <circle cx="12" cy="6" r="2" fill="white" stroke="black" />
                  <line x1="4" y1="12" x2="20" y2="12" />
                  <circle cx="8" cy="12" r="2" fill="white" stroke="black" />
                  <line x1="4" y1="18" x2="20" y2="18" />
                  <circle cx="16" cy="18" r="2" fill="white" stroke="black" />
                </svg>
              </button>
            </div>
          </div>
          {isProductsLoading ? (
            <div className={styles.productsLoadingState} aria-live="polite">
              <LoadingItem />
            </div>
          ) : (
            <>
              {screenResolution?.width >= 1025 && (
                <TableComponent
                  childComponent={<ReportTable />}
                  classnames={styles.inventoryTable}
                />
              )}
              {screenResolution?.width <= 1024 && <MobileDataGrid />}
              {displayedProductsList && (
                <CustomPagination
                  pageCount={displayedProductsList.totalNumberOfPages}
                  forcePage={displayedProductsList.pageNumber - 1}
                  onPageChange={handlePageClick}
                  pageRangeDisplayed={4}
                  renderOnZeroPageCount={null}
                  currentPageNumber={pageNumber}
                />
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="inapp-content">
      <div className="page-content dashboard">
        {isInitialProductsLoading && <LoadingItem />}
        {!isInitialProductsLoading && ReportsDisplay()}
      </div>
      {showFilterModal && (
        <FilterModal
          show={showFilterModal}
          allCategories={categoryList}
          onHide={() => setShowFilterModal(false)}
          filterValues={productFilterValues}
          setFilterValues={setProductFilterValues}
          onSearch={(filters) => {
            setProductFilterValues({
              ...productFilterValues,
              ...filters,
              minPrice: String(filters.minPrice),
              maxPrice: String(filters.maxPrice),
            });
            setSearchValue("");
            setSearchText("");
            setActiveFilters(filters);
            setPageNumber(1);
            setShowFilterModal(false);
          }}
          onReset={() => {
            setProductFilterValues(defaultProductFilterValues);
            setSearchValue("");
            setSearchText("");
            setActiveFilters(null);
            setPageNumber(1);
            setShowFilterModal(false);
          }}
        />
      )}
    </div>
  );
};

const AllProductsListManagement = (props: any) => {
  return <InAppContent pageProps={props} />;
};

const mapDispatchToProps = {
  getProductsListAction: appActions.GetProductsList,
  updateProductAction: appActions.UpdateStoreProduct,
};

const mapStateToProps = (state: any) => ({
  getProductsListRequest: state.allInAppReducers.getProductsListReducer,
  updateStoreProductRequest: state.allInAppReducers.updateStoreProductReducer,
});
export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AllProductsListManagement);
